/**
 * Home-page search-box hint experiment (owner decision, 2026-10-31).
 *
 * Three arms, only on the web home page:
 *  - `current`  — today's long hint («نام بیماری، تخصص، پزشک، بیمارستان و ...»), the baseline;
 *  - `control`  — «جستجو در پذیرش۲۴»;
 *  - `rotating` — one example at a time, each a query the live search really answers
 *                 (tested on live search 2026-10-31, ghane sa-explain), so the hint never promises
 *                 what the search cannot keep. No real doctor's name: it would favour one doctor.
 * Every other page (listing, header search) always shows the control text.
 *
 * The split is a hash of `terminal_id`, decided in the browser only: the HTML of the home page is shared
 * by the CDN, so a server-side split would hand one visitor's arm to everyone. Visitors without a terminal
 * yet and the installed app stay out and keep today's hint. The share that enters is
 * `SEARCH_HOME_HINT_PERCENT` (0..100, 0 = off), read at process start ⇒ change it in platform-gitops.
 */

export const HINT_CONTROL = 'جستجو در پذیرش۲۴';

export const HINT_ROTATING = [
  'دکتر خوب برای درد زانو',
  'سردرد دارم، کدوم دکتر برم؟',
  'دکتر اطفال برای تب بچه',
  'بیمارستان بهمن',
  'کلینیک چشم',
  'متخصص پوست و مو',
  'سونوگرافی رحم و تخمدان',
  'مشاوره ازدواج',
] as const;

export type HintArm = 'current' | 'control' | 'rotating';
const ARMS: readonly HintArm[] = ['current', 'control', 'rotating'];

/** How long one example stays, in ms (the fade-out below is part of it). */
export const HINT_ROTATE_MS = 3000;
/** The fade-out before the next example, in ms; matches `hintOut` in PlasmicSearchInput.module.css. */
export const HINT_LEAVE_MS = 200;

/** `SEARCH_HOME_HINT_PERCENT`: an integer share 0..100; anything else is 0 (off). */
export const parseHintPercent = (raw: unknown): number => {
  const n = Number.parseInt(String(raw ?? '').trim(), 10);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(n, 100);
};

const fnv1a32 = (input: string): number => {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
};

/** MurmurHash3's finaliser: spreads every input bit, so `% 3` is not tied to the `% 100` entry bucket. */
const fmix32 = (h: number): number => {
  let x = h >>> 0;
  x ^= x >>> 16;
  x = Math.imul(x, 0x85ebca6b);
  x ^= x >>> 13;
  x = Math.imul(x, 0xc2b2ae35);
  x ^= x >>> 16;
  return x >>> 0;
};

/** `null` = not in the experiment (sees today's hint, no arm on events). */
export const hintArm = ({ terminalId, percent, isApp }: { terminalId?: string | null; percent: number; isApp: boolean }): HintArm | null => {
  if (isApp || !terminalId || percent <= 0) return null;
  // Own salts: independent of the DR-56 landing split keyed on the same terminal.
  if (fnv1a32(`hint-in:${terminalId}`) % 100 >= percent) return null;
  return ARMS[fmix32(fnv1a32(`hint-arm:${terminalId}`)) % ARMS.length];
};

/** `undefined` = leave the page's own default hint. */
export const hintPlaceholder = ({ arm, step }: { arm: HintArm | null; step: number }): string | undefined => {
  if (arm === 'control') return HINT_CONTROL;
  if (arm === 'rotating') return HINT_ROTATING[step % HINT_ROTATING.length];
  return undefined;
};

export const nextHintStep = (step: number): number => (step + 1) % HINT_ROTATING.length;
